import {RemoteConstantsModel} from "../models/remoteConstants.model";
import {namespacedTypeDescriptionKey} from "./descriptionKeys";

// "Used" here means "referenced from RemoteConstants data" — entity ids,
// Metadata uids/ids, image/sound/imagePack keys, etc. Keys referenced only
// from Client (C#) code aren't tracked here; they show as unused (blue) in
// the editor, which is honest from the data's point of view. The
// `Rift/Documentation/audits/analyze-loc-usage.py` script is the source of
// truth for the broader "in use anywhere" check.
// Display marker for undefined localization keys. getLocalizationValue
// returns `[[key]]` instead of the bare key so every surface that renders a
// localized value makes the missing entry obvious to the user. Use
// isMissingLocalization to branch on it instead of comparing against the key.
export function missingLocalization(key: string): string {
    return `[[${key}]]`;
}

export function isMissingLocalization(value: string): boolean {
    return value.startsWith('[[') && value.endsWith(']]');
}

export function getUsedLocalizationKeys(state: RemoteConstantsModel): Set<string> {
    const keys = new Set<string>();

    // `loc.` prefix = key owned by Client code (RiftConstants LANG_KEY_*).
    // Always considered used; everything else must be referenced from data.
    for (const lang of Object.values(state.localization ?? {})) {
        for (const key of Object.keys(lang)) {
            if (key.startsWith('loc.')) keys.add(key);
        }
    }

    // Special constants
    if (state.startingUnit) keys.add(state.startingUnit);
    if (state.startingBuilding) keys.add(state.startingBuilding);

    // Units. Entity description keys (`<id>.description`) are real lookups:
    // the Client builds them for info popups (Building.cs, UnlockablePopup)
    // and the editor's entity views edit them — same pattern for buildings,
    // tiles, and resources below.
    if (state.units) {
        for (const unit of state.units) {
            keys.add(unit.id);
            keys.add(`${unit.id}.description`);
            if (unit.attributes) {
                for (const attr of unit.attributes) keys.add(attr);
            }
        }
    }

    // Buildings
    if (state.buildings) {
        for (const building of state.buildings) {
            keys.add(building.id);
            keys.add(`${building.id}.description`);
            if (building.attributes) {
                for (const attr of building.attributes) keys.add(attr);
            }
            if (building.produceableUnits) {
                for (const uid of building.produceableUnits) keys.add(uid);
            }
            if (building.rewards) {
                for (const level of building.rewards) {
                    if (level) {
                        for (const reward of level) keys.add(reward);
                    }
                }
            }
        }
    }

    // Tiles
    if (state.tiles) {
        for (const tile of state.tiles) {
            keys.add(tile.id);
            keys.add(`${tile.id}.description`);
            if (tile.supportingTile) keys.add(tile.supportingTile);
            if (tile.tileType) keys.add(tile.tileType);
            if (tile.resources) {
                for (const r of tile.resources) keys.add(r);
            }
            if (tile.buildings) {
                for (const b of tile.buildings) keys.add(b);
            }
        }
    }

    // Resources
    if (state.resources) {
        for (const resource of state.resources) {
            keys.add(resource.id);
            keys.add(`${resource.id}.description`);
            if (resource.attributes) {
                for (const attr of resource.attributes) keys.add(attr);
            }
        }
    }

    // Tech tree
    if (state.techTree) {
        for (const node of state.techTree) {
            keys.add(node.id);
            keys.add(`${node.id}.description`);
            if (node.unlocks) {
                for (const unlock of node.unlocks) keys.add(unlock);
            }
            if (node.children) {
                for (const child of node.children) keys.add(child);
            }
        }
    }

    // All metadata collections: uid as instance name, uid.description as the
    // per-instance description, id.description as the shared fallback.
    const metadataCollections = [
        state.unitAttributes,
        state.buildingAttributes,
        state.techActionAttributes,
        state.techPassiveAttributes,
        state.rewards,
    ];
    const descriptionIds = new Set<string>();
    for (const collection of metadataCollections) {
        if (!collection) continue;
        for (const meta of collection) {
            if (meta.uid) {
                keys.add(meta.uid);
                keys.add(`${meta.uid}.description`);
            }
            if (meta.id) {
                descriptionIds.add(meta.id);                                // legacy bare key (transitional)
                // Namespaced key needs the uid's first segment — skip it for
                // legacy records that predate uids (e.g. older branches).
                if (meta.uid) keys.add(namespacedTypeDescriptionKey(meta.uid, meta.id));  // new namespaced key
            }
        }
    }
    // Legacy <type>.description keys stay "expected" through the migration so
    // they aren't flagged as orphans before the purge step removes them.
    descriptionIds.forEach(id => keys.add(`${id}.description`));

    // Asset keys
    if (state.images) {
        for (const key of Object.keys(state.images)) keys.add(key);
    }
    if (state.sounds) {
        for (const key of Object.keys(state.sounds)) keys.add(key);
    }
    if (state.imagePacks) {
        for (const key of Object.keys(state.imagePacks)) keys.add(key);
    }

    return keys;
}
